import React, { Component } from 'react';
import axios from 'axios';
import {
    Typography,
    Button,
    TextField,
    Checkbox,
    FormControlLabel,
    MenuItem,
    CircularProgress
} from '@material-ui/core';
import DrawerMenuRight from '../../Common/DrawerMenuRight';
import {
    COUNTRIES,
    US_STATES,
    CONTINENTAL_48_STATE_CODES,
} from '../../../utils/geo';

const apiBaseUrl = process.env.REACT_APP_API_URL || '';

const emptyRule = () => ({
    name: '',
    code: '',
    autoApply: false,
    type: 'percent_off_order',
    value: 0,
    minSubtotal: '',
    startsAtUtc: '',
    endsAtUtc: '',
    isActive: true,
    maxUsesGlobal: '',
    maxUsesPerCustomer: '',
    isCustomerSpecific: false,
    customerUserId: '',
    allowedCountryCodes: [],
    allowedUsStateCodes: [],
});

const fieldStyle = { marginBottom: 12 };
const rowStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    marginBottom: 0,
};

const formatLocationSummary = (rule) => {
    const countries = rule.allowedCountryCodes || [];
    if (countries.length === 0) return 'Everywhere';
    const states = rule.allowedUsStateCodes || [];
    if (countries.includes('US') && states.length > 0) {
        const other = countries.filter((c) => c !== 'US');
        const usPart = states.length === CONTINENTAL_48_STATE_CODES.length
            && CONTINENTAL_48_STATE_CODES.every((c) => states.includes(c))
            ? 'US (Continental 48)'
            : `US (${states.length} states)`;
        return [usPart, ...other].join(', ');
    }
    return countries.join(', ');
};

class CustomerDiscountRulesPanel extends Component {
    state = {
        loading: false,
        saving: false,
        rules: [],
        editorOpen: false,
        editingRuleId: null,
        form: emptyRule(),
    };

    componentDidMount() {
        this.loadRules();
    }

    mapRuleFromApi = (rule) => ({
        ...emptyRule(),
        ...rule,
        minSubtotal: rule.minSubtotal ?? '',
        maxUsesGlobal: rule.maxUsesGlobal ?? '',
        maxUsesPerCustomer: rule.maxUsesPerCustomer ?? '',
        startsAtUtc: rule.startsAtUtc || '',
        endsAtUtc: rule.endsAtUtc || '',
        customerUserId: rule.customerUserId || '',
        allowedCountryCodes: Array.isArray(rule.allowedCountryCodes) ? rule.allowedCountryCodes : [],
        allowedUsStateCodes: Array.isArray(rule.allowedUsStateCodes) ? rule.allowedUsStateCodes : [],
    });

    loadRules = async () => {
        this.setState({ loading: true });
        try {
            const { data } = await axios.get(`${apiBaseUrl}customer/discounts/admin`);
            this.setState({
                rules: Array.isArray(data) ? data.map(this.mapRuleFromApi) : [],
                loading: false
            });
        } catch (error) {
            console.error('Error loading customer discount rules:', error);
            window.$alertError('Failed to load discount rules');
            this.setState({ loading: false });
        }
    };

    normalizePayload = (rule) => ({
        name: (rule.name || '').trim(),
        code: rule.autoApply ? null : ((rule.code || '').trim() || null),
        autoApply: !!rule.autoApply,
        type: rule.type,
        value: Number(rule.value || 0),
        minSubtotal: rule.minSubtotal === '' ? null : Number(rule.minSubtotal || 0),
        startsAtUtc: rule.startsAtUtc || null,
        endsAtUtc: rule.endsAtUtc || null,
        isActive: !!rule.isActive,
        maxUsesGlobal: rule.maxUsesGlobal === '' ? null : Number(rule.maxUsesGlobal || 0),
        maxUsesPerCustomer: rule.maxUsesPerCustomer === '' ? null : Number(rule.maxUsesPerCustomer || 0),
        isCustomerSpecific: !!rule.isCustomerSpecific,
        customerUserId: rule.isCustomerSpecific ? ((rule.customerUserId || '').trim() || null) : null,
        allowedCountryCodes: Array.isArray(rule.allowedCountryCodes) ? rule.allowedCountryCodes : [],
        allowedUsStateCodes: Array.isArray(rule.allowedUsStateCodes) ? rule.allowedUsStateCodes : [],
    });

    validate = (rule) => {
        if (!rule.name || !rule.name.trim()) {
            window.$alertError('Discount name is required');
            return false;
        }
        if (!rule.type) {
            window.$alertError('Discount type is required');
            return false;
        }
        if (!rule.autoApply && !rule.code?.trim()) {
            window.$alertError('Code is required when Auto Apply is disabled');
            return false;
        }
        if (rule.isCustomerSpecific && !rule.customerUserId?.trim()) {
            window.$alertError('Customer User ID is required for customer-specific discounts');
            return false;
        }
        return true;
    };

    openCreate = () => {
        this.setState({
            editorOpen: true,
            editingRuleId: null,
            form: emptyRule(),
        });
    };

    openEdit = (rule) => {
        this.setState({
            editorOpen: true,
            editingRuleId: rule.id,
            form: this.mapRuleFromApi(rule),
        });
    };

    closeEditor = () => {
        this.setState({
            editorOpen: false,
            editingRuleId: null,
            form: emptyRule(),
        });
    };

    handleSave = async () => {
        const { form, editingRuleId } = this.state;
        if (!this.validate(form)) return;

        this.setState({ saving: true });
        try {
            if (editingRuleId) {
                await axios.put(
                    `${apiBaseUrl}customer/discounts/admin/${encodeURIComponent(editingRuleId)}`,
                    this.normalizePayload(form)
                );
                window.$alertSuccess('Discount rule updated');
            } else {
                await axios.post(`${apiBaseUrl}customer/discounts/admin`, this.normalizePayload(form));
                window.$alertSuccess('Discount rule created');
            }
            this.setState({ saving: false });
            this.closeEditor();
            await this.loadRules();
        } catch (error) {
            console.error('Error saving discount rule:', error);
            window.$alertError(error?.response?.data || 'Failed to save discount rule');
            this.setState({ saving: false });
        }
    };

    handleDelete = async (ruleId) => {
        if (!window.confirm('Delete this discount rule?')) return;

        this.setState({ saving: true });
        try {
            await axios.delete(`${apiBaseUrl}customer/discounts/admin/${encodeURIComponent(ruleId)}`);
            window.$alertSuccess('Discount rule deleted');
            this.setState({ saving: false });
            await this.loadRules();
        } catch (error) {
            console.error('Error deleting discount rule:', error);
            window.$alertError('Failed to delete discount rule');
            this.setState({ saving: false });
        }
    };

    setForm = (patch) => {
        this.setState((prev) => ({
            form: {
                ...prev.form,
                ...patch,
            },
        }));
    };

    toggleCode = (field, code) => {
        this.setState((prev) => {
            const current = Array.isArray(prev.form[field]) ? prev.form[field] : [];
            const next = current.includes(code)
                ? current.filter((c) => c !== code)
                : [...current, code];
            return {
                form: {
                    ...prev.form,
                    [field]: next,
                },
            };
        });
    };

    renderCheckboxGroup = (title, items, field, extras, helpText) => {
        const { form } = this.state;
        const selected = Array.isArray(form[field]) ? form[field] : [];
        return (
            <div style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2">{title}</Typography>
                    <div>
                        {extras}
                        <Button size="small" onClick={() => this.setForm({ [field]: items.map((i) => i.code) })}>Select all</Button>
                        <Button size="small" onClick={() => this.setForm({ [field]: [] })}>Select none</Button>
                    </div>
                </div>
                {helpText && (
                    <Typography variant="caption" style={{ color: '#666', display: 'block', marginTop: 2 }}>
                        {helpText}
                    </Typography>
                )}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                    maxHeight: 220,
                    overflow: 'auto',
                    border: '1px solid #ddd',
                    padding: 8,
                    marginTop: 8,
                    backgroundColor: '#fff',
                }}
                >
                    {items.map((item) => (
                        <FormControlLabel
                            key={item.code}
                            control={(
                                <Checkbox
                                    color="primary"
                                    checked={selected.includes(item.code)}
                                    onChange={() => this.toggleCode(field, item.code)}
                                />
                            )}
                            label={item.name}
                        />
                    ))}
                </div>
            </div>
        );
    };

    renderEditorForm = () => {
        const { form, saving, editingRuleId } = this.state;
        const countries = form.allowedCountryCodes || [];
        const showStates = countries.length === 0 || countries.includes('US');

        return (
            <div style={{ padding: 20 }}>
                <TextField
                    label="Name"
                    value={form.name || ''}
                    onChange={(e) => this.setForm({ name: e.target.value })}
                    fullWidth
                    variant="outlined"
                    style={fieldStyle}
                />

                <div style={rowStyle}>
                    <TextField
                        select
                        label="Type"
                        value={form.type || 'percent_off_order'}
                        onChange={(e) => this.setForm({ type: e.target.value })}
                        fullWidth
                        variant="outlined"
                        style={fieldStyle}
                    >
                        <MenuItem value="percent_off_order">Percent Off Order</MenuItem>
                        <MenuItem value="amount_off_order">Amount Off Order</MenuItem>
                        <MenuItem value="percent_off_shipping">Percent Off Shipping</MenuItem>
                        <MenuItem value="amount_off_shipping">Amount Off Shipping</MenuItem>
                    </TextField>
                    <TextField
                        label="Value"
                        type="number"
                        value={form.value ?? 0}
                        onChange={(e) => this.setForm({ value: e.target.value })}
                        fullWidth
                        variant="outlined"
                        style={fieldStyle}
                    />
                </div>

                <div style={rowStyle}>
                    <TextField
                        label="Code"
                        value={form.code || ''}
                        disabled={!!form.autoApply}
                        onChange={(e) => this.setForm({ code: e.target.value })}
                        fullWidth
                        variant="outlined"
                        style={fieldStyle}
                        helperText={form.autoApply ? 'Not used when Auto Apply is on' : ' '}
                    />
                    <TextField
                        label="Min Subtotal"
                        type="number"
                        value={form.minSubtotal ?? ''}
                        onChange={(e) => this.setForm({ minSubtotal: e.target.value })}
                        fullWidth
                        variant="outlined"
                        style={fieldStyle}
                    />
                </div>

                <div style={rowStyle}>
                    <TextField
                        label="Starts At (UTC)"
                        type="datetime-local"
                        value={form.startsAtUtc || ''}
                        onChange={(e) => this.setForm({ startsAtUtc: e.target.value })}
                        fullWidth
                        variant="outlined"
                        style={fieldStyle}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="Ends At (UTC)"
                        type="datetime-local"
                        value={form.endsAtUtc || ''}
                        onChange={(e) => this.setForm({ endsAtUtc: e.target.value })}
                        fullWidth
                        variant="outlined"
                        style={fieldStyle}
                        InputLabelProps={{ shrink: true }}
                    />
                </div>

                <div style={rowStyle}>
                    <TextField
                        label="Max Uses (Global)"
                        type="number"
                        value={form.maxUsesGlobal ?? ''}
                        onChange={(e) => this.setForm({ maxUsesGlobal: e.target.value })}
                        fullWidth
                        variant="outlined"
                        style={fieldStyle}
                    />
                    <TextField
                        label="Max Uses (Per Customer)"
                        type="number"
                        value={form.maxUsesPerCustomer ?? ''}
                        onChange={(e) => this.setForm({ maxUsesPerCustomer: e.target.value })}
                        fullWidth
                        variant="outlined"
                        style={fieldStyle}
                    />
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginBottom: 8 }}>
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={!!form.autoApply}
                                onChange={(e) => this.setForm({ autoApply: e.target.checked })}
                                color="primary"
                            />
                        )}
                        label="Auto Apply"
                    />
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={!!form.isActive}
                                onChange={(e) => this.setForm({ isActive: e.target.checked })}
                                color="primary"
                            />
                        )}
                        label="Active"
                    />
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={!!form.isCustomerSpecific}
                                onChange={(e) => this.setForm({ isCustomerSpecific: e.target.checked })}
                                color="primary"
                            />
                        )}
                        label="Customer Specific"
                    />
                </div>

                {form.isCustomerSpecific && (
                    <TextField
                        label="Customer User ID"
                        value={form.customerUserId || ''}
                        onChange={(e) => this.setForm({ customerUserId: e.target.value })}
                        fullWidth
                        variant="outlined"
                        style={fieldStyle}
                        helperText="Required for customer-specific rules"
                    />
                )}

                <Typography variant="subtitle2" style={{ marginTop: 8 }}>Location restrictions</Typography>
                <Typography variant="caption" style={{ color: '#666', display: 'block', marginBottom: 4 }}>
                    Optional. Empty country list means the code works everywhere.
                </Typography>

                {this.renderCheckboxGroup(
                    'Allowed countries',
                    COUNTRIES,
                    'allowedCountryCodes',
                    null,
                    'Selecting countries restricts the code to those destinations.'
                )}
                {showStates && this.renderCheckboxGroup(
                    'Allowed US states',
                    US_STATES,
                    'allowedUsStateCodes',
                    (
                        <Button
                            size="small"
                            onClick={() => this.setForm({ allowedUsStateCodes: [...CONTINENTAL_48_STATE_CODES] })}
                        >
                            Continental 48
                        </Button>
                    ),
                    'Only used when US is allowed (or unrestricted). Leave empty for all US states.'
                )}

                <div style={{ marginTop: 24, display: 'flex', gap: 8 }}>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={this.handleSave}
                        disabled={saving}
                    >
                        {saving ? <CircularProgress size={18} /> : (editingRuleId ? 'Save Changes' : 'Create Rule')}
                    </Button>
                    <Button variant="outlined" onClick={this.closeEditor} disabled={saving}>
                        Cancel
                    </Button>
                </div>
            </div>
        );
    };

    render() {
        const { loading, saving, rules, editorOpen, editingRuleId } = this.state;

        return (
            <div>
                <h2 style={{ marginBottom: 8, marginTop: 0 }}>
                    Customer Discount Rules
                </h2>
                <Typography variant="body2" style={{ marginBottom: 12, color: '#666' }}>
                    Checkout discount codes and auto-apply rules. Optionally restrict codes by shipping country/state.
                    For free shipping, use a free shipping method under Shipping Methods instead.
                </Typography>

                <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={this.openCreate}
                        disabled={saving}
                    >
                        Add Rule
                    </Button>
                    <Button variant="outlined" onClick={this.loadRules} disabled={loading || saving}>
                        Refresh
                    </Button>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: 20 }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <div className="parts-table-container">
                        <table className="parts-table">
                            <thead>
                                <tr>
                                    <th style={{ minWidth: 170 }}>Name</th>
                                    <th style={{ minWidth: 130 }}>Type</th>
                                    <th style={{ minWidth: 90 }}>Value</th>
                                    <th style={{ minWidth: 120 }}>Code</th>
                                    <th style={{ minWidth: 160 }}>Locations</th>
                                    <th style={{ minWidth: 95 }}>Auto</th>
                                    <th style={{ minWidth: 95 }}>Active</th>
                                    <th style={{ minWidth: 130 }}>Usage</th>
                                    <th style={{ minWidth: 170 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rules.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} style={{ textAlign: 'center', padding: 20, color: '#777' }}>
                                            No discount rules found.
                                        </td>
                                    </tr>
                                ) : rules.map((rule) => (
                                    <tr key={rule.id}>
                                        <td>{rule.name}</td>
                                        <td>{rule.type}</td>
                                        <td>{rule.value}</td>
                                        <td>{rule.code || '-'}</td>
                                        <td style={{ fontSize: 12, color: '#444' }}>{formatLocationSummary(rule)}</td>
                                        <td style={{ textAlign: 'center' }}>{rule.autoApply ? 'Yes' : 'No'}</td>
                                        <td style={{ textAlign: 'center' }}>{rule.isActive ? 'Yes' : 'No'}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            {rule.usedCount || 0}
                                            {rule.isCustomerSpecific ? ` / ${rule.customerUsageCount ?? 0}` : ''}
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: 8 }}>
                                                <Button size="small" variant="outlined" onClick={() => this.openEdit(rule)} disabled={saving}>
                                                    Edit
                                                </Button>
                                                <Button size="small" variant="outlined" onClick={() => this.handleDelete(rule.id)} disabled={saving}>
                                                    Delete
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {editorOpen && (
                    <DrawerMenuRight
                        title={editingRuleId ? 'Edit discount rule' : 'New discount rule'}
                        open={editorOpen}
                        onClose={this.closeEditor}
                        customWidth={720}
                    >
                        {this.renderEditorForm()}
                    </DrawerMenuRight>
                )}
            </div>
        );
    }
}

export default CustomerDiscountRulesPanel;
